import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { LocalizationContext } from '../shared/contexts'
import { CatalogueActions, createCatalogueActions } from './catalogueActions'
import { CatalogueState, catalogueReducer, initialCatalogueState } from './catalogueReducer'
import { findCategory, findItem, rootIdOf } from './catalogueSelectors'
import { CatalogueItem, CategoryNode, ItemKind, ItemRef, OrderProvider, Panel, WeightUnit, Workspace } from './catalogueTypes'
import {
  browsePath,
  categoryPath,
  editItemPath,
  hideItemPath,
  itemRefOf,
  newItemPath,
  panelKey,
  showItemPath
} from './catalogueUrls'

export type ViewPanel =
  | { kind: 'browse' }
  | { kind: 'new'; type: ItemKind }
  | { kind: 'edit'; item: CatalogueItem }

export interface CatalogueViewState
  extends Pick<
    CatalogueState,
    | 'roots'
    | 'categoryOptions'
    | 'columns'
    | 'productAttributes'
    | 'treeLoading'
    | 'rowsLoadingFor'
    | 'editMode'
    | 'expandedTableCategoryIds'
    | 'deletion'
    | 'notice'
  > {
  categoryId: number | null
  selectedCategory: CategoryNode | null
  rows: CatalogueItem[]
  panel: ViewPanel
  panelKey: string
  panelUnresolved: boolean
  show: ItemRef | null
  expandedRootId: number | null
}

export interface CataloguePaths {
  browse(): string
  category(id: number | null): string
  newItem(type: ItemKind): string
  edit(item: CatalogueItem): string
  show(item: CatalogueItem, categoryId?: number | null): string
  hide(): string
}

export interface CatalogueMeta {
  orderProviders: OrderProvider[]
  weightUnit: WeightUnit
  paths: CataloguePaths
  go: { [K in keyof CataloguePaths]: (...args: Parameters<CataloguePaths[K]>) => void }
}

interface CatalogueContextValue {
  state: CatalogueViewState
  actions: CatalogueActions
  meta: CatalogueMeta
}

const CatalogueContext = createContext<CatalogueContextValue | null>(null)

export const useCatalogue = (): CatalogueContextValue => {
  const value = useContext(CatalogueContext)
  if (!value) throw new Error('useCatalogue must be used inside CatalogueProvider')
  return value
}

const resolvePanel = (panel: Panel, rows: CatalogueItem[], roots: CategoryNode[]): ViewPanel => {
  if (panel.kind !== 'edit') return panel
  const item = findItem(rows, roots, panel.item)
  return item ? { kind: 'edit', item } : { kind: 'browse' }
}

const toUrlPanel = (panel: ViewPanel): Panel =>
  panel.kind === 'edit' ? { kind: 'edit', item: itemRefOf(panel.item) } : panel

interface CatalogueProviderProps {
  categoryId: number | null
  workspace: Workspace
  orderProviders: OrderProvider[]
  navigate: (path: string) => void
  children: React.ReactNode
}

const CatalogueProvider = ({ categoryId, workspace, orderProviders, navigate, children }: CatalogueProviderProps) => {
  const [state, dispatch] = useReducer(catalogueReducer, initialCatalogueState)
  const actions = useMemo(() => createCatalogueActions(dispatch), [])
  const { weight_scale } = useContext(LocalizationContext)

  useEffect(() => {
    actions.bootstrap()
  }, [actions])

  useEffect(() => {
    if (categoryId === null) return
    const controller = new AbortController()
    actions.loadRows(categoryId, controller.signal)
    return () => controller.abort()
  }, [actions, categoryId])

  const selectedCategory = findCategory(state.roots, categoryId)
  const rows = categoryId === null ? [] : state.rowsByCategoryId[categoryId] ?? []
  const panel = resolvePanel(workspace.panel, rows, state.roots)
  const urlPanel = toUrlPanel(panel)

  useEffect(() => {
    if (panel.kind !== 'browse' && state.editMode) actions.toggleEditMode()
  }, [actions, panel.kind, state.editMode])

  const override = state.sidebarOverride
  const expandedRootId =
    override && override.forCategoryId === categoryId ? override.expandedRootId : rootIdOf(selectedCategory)

  const paths: CataloguePaths = {
    browse: () => browsePath(categoryId),
    category: (id) => categoryPath(id),
    newItem: (type) => newItemPath(categoryId, type),
    edit: (item) => editItemPath(categoryId, itemRefOf(item)),
    show: (item, inCategoryId = categoryId) => showItemPath(inCategoryId, urlPanel, itemRefOf(item)),
    hide: () => hideItemPath(categoryId, urlPanel)
  }

  const value: CatalogueContextValue = {
    state: {
      roots: state.roots,
      categoryOptions: state.categoryOptions,
      columns: state.columns,
      productAttributes: state.productAttributes,
      treeLoading: state.treeLoading,
      rowsLoadingFor: state.rowsLoadingFor,
      editMode: state.editMode,
      expandedTableCategoryIds: state.expandedTableCategoryIds,
      deletion: state.deletion,
      notice: state.notice,
      categoryId,
      selectedCategory,
      rows,
      panel,
      panelKey: panelKey(urlPanel),
      panelUnresolved: panelKey(urlPanel) !== panelKey(workspace.panel),
      show: workspace.show,
      expandedRootId
    },
    actions,
    meta: {
      orderProviders,
      weightUnit: weight_scale === 'metric' ? 'g' : 'oz',
      paths,
      go: {
        browse: () => navigate(paths.browse()),
        category: (id) => navigate(paths.category(id)),
        newItem: (type) => navigate(paths.newItem(type)),
        edit: (item) => navigate(paths.edit(item)),
        show: (item, inCategoryId) => navigate(paths.show(item, inCategoryId)),
        hide: () => navigate(paths.hide())
      }
    }
  }

  return <CatalogueContext.Provider value={value}>{children}</CatalogueContext.Provider>
}

export default CatalogueProvider
